import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '../../../../services/theme.service';
import { Location } from '@angular/common';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { User } from '../../../../model/user';
import { SpinnerService } from '../../../../services/spinner.service';
import { CalibrationBalance, CalibrationBalanceFilter } from '../../../../model/calibrationBalance';
import { Subject, takeUntil } from 'rxjs';
import { LangFlag } from '../../../../services/translation.service';
import { CalibrationStatus } from '../../../../model/calibration';
import { CalibrationBalanceDataSource } from '../../../../model/calibrationBalanceDataSource';
import { CalibrationBalanceService } from '../../../../services/calibrationBalance.service';
import { SnackBarService } from '../../../../services/snackBar.service';
import { ConfirmationDialogComponent } from '../../../shared/confirmation-dialog/confirmation-dialog.component';
import { UserService } from '../../../../services/user.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { CalibrationBalanceEditGenerateReportComponent } from '../calibration-balance-edit-generate-report/calibration-balance-edit-generate-report.component';

@Component({
  selector: 'app-calibration-balance-list',
  templateUrl: './calibration-balance-list.component.html',
  styleUrl: './calibration-balance-list.component.scss',
  standalone: false
})
export class CalibrationBalanceListComponent {

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  dataSource: CalibrationBalanceDataSource;
  filter: CalibrationBalanceFilter = new CalibrationBalanceFilter();

  public checkedItems = new Map<CalibrationBalance, boolean>();

  currentUser: User;

  hasFiltered = false;
  langs: LangFlag[];

  displayedColumns: string[] = [
    'checkbox', 'projectNo', 'certificateNum', 'client', 'name', 'equipment', 'serialNum', 'status', 'calibrateDate', 'expirationDate',
    'realizationDate', 'download', 'edit', 'delete', 'more'
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private location: Location,
    private calibrationBalanceService: CalibrationBalanceService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private themeService: ThemeService,
    private spinnerService: SpinnerService,
    private snackBarService: SnackBarService,
    private userService: UserService) {

  }

  get pageTitle(): string {
    return this.translate.instant('calibrateEquipmentList.title') as string;
  }

  ngOnInit(): void {
    this.currentUser = this.userService.currentProfile;

    const excludedCols: string[] = [];

    if (!this.currentUser.showClone) {
      excludedCols.push('clone');
    }

    this.displayedColumns = this.displayedColumns.filter(c => !excludedCols.includes(c));

    const filterResult = localStorage.getItem('filter');
    if (filterResult) {
      this.hasFiltered = true;
      this.filter = JSON.parse(filterResult) as CalibrationBalanceFilter;
    }
    this.loadCalibrationList();
  }

  getThemeIconPath(): string {
    const url = this.router.url;
    return this.themeService.getThemeIconPathSelection(url);
  }

  newCalibration(): void {
    void this.router.navigateByUrl('balances/calibration?id=0');
  }

  editRow(id: number): void {
    let url: string;
    if (this.router.url.includes('calibrates')) {
      url = `calibrates/calibration?id=${id}`
    } else if (this.router.url.includes('thermal')) {
      url = `thermal/calibration?id=${id}`
    } else if (this.router.url.includes('balances')) {
      url = `balances/calibration?id=${id}`
    }
    void this.router.navigateByUrl(url);
  }

  deleteRow(id: number): void {
    if (id < 0) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: this.translate.instant('calibrateEquipmentList.dialog.delete.confirmation') as string
      }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result === true) {
        this.spinnerService.show();

        this.calibrationBalanceService.delete(id).pipe(takeUntil(this.destroy$)).subscribe(() => {
          this.loadCalibrationList();

          this.snackBarService.sendSuccess(this.translate.instant('calibrateEquipmentList.dialog.delete.ok') as string);

          this.spinnerService.hide();
        }, () => {
          this.snackBarService.sendError(this.translate.instant('calibrateEquipmentList.dialog.delete.error') as string);

          this.spinnerService.hide();
        });
      }
    });

  }

  canClone(calibration: CalibrationBalance): boolean {
    return calibration?.idStatus === CalibrationStatus.FIRMADO;
  }

  cancel(): void {
    this.location.back();
  }

  highlight(calibration: CalibrationBalance): string {
    return this.isCanceled(calibration) ? 'deactivate' : 'action';
  }

  doFilter(event: CalibrationBalanceFilter): void {
    this.filter = event;

    this.hasFiltered = true;

    this.paginator.pageIndex = 0;
    this.loadCalibrationList();
  }

  loadCalibrationList(): void {
    this.checkedItems = new Map<CalibrationBalance, boolean>();

    this.dataSource = new CalibrationBalanceDataSource(this.calibrationBalanceService);

    this.filter.sortBy = this.sort.active || 'calibrateDate';
    this.filter.sortDirection = this.sort.direction || 'desc';
    this.filter.pageIndex = this.paginator.pageIndex || 0;
    this.filter.pageSize = this.paginator.pageSize || 20;

    if (this.hasFiltered) {
      this.saveSearchFilter(this.filter);
      this.dataSource.loadCalibration(this.filter);
    }
    this.dataSource.loading$.pipe(takeUntil(this.destroy$)).subscribe(message => this.spinnerService.next(message));
  }

  private isCanceled(calibration: CalibrationBalance): boolean {
    return calibration.idStatus === CalibrationStatus.NO_VALIDO;
  }

  saveSearchFilter(execution: CalibrationBalanceFilter): void {
    localStorage.setItem('filter', JSON.stringify(execution));
  }

  showSelectAll(): boolean {
    return this.dataSource.data.length > 0;
  }

  isAllSelected(): boolean {
    return this.dataSource.data.every(item => this.isSelected(item));
  }

  isSelected(item: CalibrationBalance): boolean {
    if (item == null) {
      return false;
    }

    return this.checkedItems.get(item);
  } 

  showCheckbox(calibration: CalibrationBalance): boolean {
    const allowedStatuses = [CalibrationStatus.EN_EJECUCION, CalibrationStatus.PENDIENTE_FIRMA, CalibrationStatus.FIRMADO];

    return allowedStatuses.includes(calibration.idStatus);
  }

   onCheckItem(event: MatCheckboxChange): void {
    const elem = event.source.value as unknown as CalibrationBalance;
    this.checkedItems.set(elem, event.checked);
  }

  onCheckAllItems(event: MatCheckboxChange): void {
    this.dataSource.data.forEach(elem => {
      if (CalibrationStatus.NO_VALIDO !== elem.idStatus) {
        this.checkedItems.set(elem, event.checked);
      }
    });
  }

  showAutomaticSign(): boolean {
    const current = this.currentUser;

    return current.automaticSign;
  }

  canDelete(calibration: CalibrationBalance): boolean {
    const allowedStatuses = [CalibrationStatus.EN_EJECUCION];

    return allowedStatuses.includes(calibration.idStatus) && +calibration.signedVersion === 0;
  }

  canDownload(calibration: CalibrationBalance): boolean {
    const allowedStatusesDownload = [CalibrationStatus.PENDIENTE_FIRMA, CalibrationStatus.FIRMADO];

    return allowedStatusesDownload.includes(calibration.idStatus);
  }

  downloadPdf(calibration: CalibrationBalance): void {
    this.dialog.open(CalibrationBalanceEditGenerateReportComponent, {
      minWidth: '20%',
      maxHeight: '95vh',
      data: {
        calibration,
        toSign: false
      }
    });
  }
}